<template lang="pug">
.show-actions(v-if="handlers.length > 0 || crud.length > 0")
  q-btn.handlers-button(flat, no-caps)
    template
      .handlers-button-content
        .handlers-button-content {{ label }}
        q-icon(name="expand_more", size="20px")
    q-menu#actions-menu
      q-list
        q-item(
          v-for="item in handlers",
          :key="item.name || item.label",
          :clickable="!isActionDisabled(item)",
          :class="{ 'action-item--disabled': isActionDisabled(item) }",
          :aria-disabled="isActionDisabled(item)",
          v-close-popup="!isActionDisabled(item)",
          @click="clickHandler(item)"
        )
          q-item-section
            q-item-label {{ item.label }}
          q-tooltip(
            v-if="getDisabledActionTooltip(item)",
            content-class="action-disable-tooltip",
            anchor="top middle",
            self="bottom middle"
          )
            span {{ getDisabledActionTooltip(item) }}
        q-separator(v-if="handlers.length > 0")
        q-item(
          clickable,
          v-close-popup,
          v-for="item in crud",
          :key="item.name || item.label",
          @click="clickCrud(item)"
        )
          q-item-section
            q-item-label {{ item.label }}

  q-dialog(v-model="showConfirm")
    confirm-modal(@confirm="confirmDelete", :parentData="{ message: notifies.confirm_destroy_entries }")
</template>

<script setup>
import { ref, computed, inject } from "vue";
import backend from "@/api";
import { handleError } from "@/services/handleErrors";
import { actionsFn } from "@/services/useActions";
import { useDisabledActions } from "@/services/useDisabledActions";
import confirmModal from "../../../helpers/confirmModal.vue";
import { notifies } from "@/services/useLocales";

const openPrintAct = inject("openPrintAct", data => emit("open-print-act", data));
const updateRow = inject("updateRow", () => {});

const props = defineProps({
  /** id текущего выбранного ряда. Нужен для обновления текущего ряда при изменении. Пример: 1 */
  rowId: { type: null || Number, default: null },
  /** Все экшены текущего ряда. Пример:
  {
    "id": "actions",
    "label": "Действия",
    "type": "actions",
    "data_url": null,
    "components": {
        "handlers": [
            {
              "action_type": "request",
              "name": "on_approve",
              "label": "Отправить на согласование",
              "style": "primary",
              "verb": "patch",
              "path": "/acts/9/on_approve"
            },
            {
              "action_type": "js",
              "name": "print",
              "label": "Печать",
              "style": "primary",
              "onclick": "printAct",
              "verb": "get",
              "data_url": "/acts/9/print"
            }
        ],
        "crud": [],
        "buttons": []
    }
  }
   */
  data: { type: Object, default: () => {} },
});

const emit = defineEmits([
  /** Используется для печати акта. Принимает дату для печати акта */
  "open-print-act",
  /** Используется для сброса даты текущего show компонента. Не принимает параметры */
  "reload-show-ui",
  /** Эмит для открытия edit компонента страницы. В качестве параметра принимает id элемента. Пример: 1 */
  "open-edit",
  /** Эмит для открытия формы создания заявки из обращения */
  "open-issue-form",
  /** Эмит удаления элемента таблицы. В качестве параметра принимает id элемента. Пример: 1 */
  "destroy-table-item",
  /** Эмит копирования элемента. В качестве параметра принимает id исходного элемента. Пример: 1 */
  "copy-row-item",
  /** Эмит открытия предпросмотра чеклиста. В качестве параметра принимает action с data_url. */
  "show-checklist",
]);

const showConfirm = ref(false);

const rowId = computed(() => props.rowId);
const label = computed(() => props.data.label);
const handlers = computed(() => props.data?.components?.handlers || []);
const crud = computed(() => props.data?.components?.crud || []);
const { isActionDisabled, getDisabledActionTooltip } = useDisabledActions(handlers);

const runRequestAction = async action => {
  try {
    const path = `/api/v3${action.path}`;

    if (action.verb === "patch") {
      await backend.patch(path, {});
    } else if (action.verb === "post") {
      await backend.create(path, {});
    } else {
      await backend.index(path, {});
    }
  } catch (err) {
    await handleError(err);
  } finally {
    emit("reload-show-ui");
    updateRow(rowId.value);
  }
};

const clickHandler = async handler => {
  if (isActionDisabled(handler)) return;

  if (handler.name === "openIssueForm") {
    emit("open-issue-form", rowId.value);
    return;
  }

  if (handler.action_type === "request") {
    await runRequestAction(handler);
  } else if (handler.action_type === "js") {
    if (handler.onclick === "printAct") {
      const printData = await actionsFn?.[handler.onclick]?.(handler.data_url);
      openPrintAct(printData);
    } else if (handler.onclick === "showChecklist") {
      emit("show-checklist", handler);
    } else if (handler.onclick === "copyJson") {
      await actionsFn?.[handler.onclick]?.(handler.data_url);
    } else {
      actionsFn?.[handler.onclick]?.();
    }
  }
};

const confirmDelete = confirm => {
  confirm && emit("destroy-table-item", rowId.value);
  showConfirm.value = false;
};

const clickCrud = async crud => {
  if (crud.name === "edit") {
    emit("open-edit", rowId.value);
  } else if (crud.name === "destroy") {
    showConfirm.value = true;
  } else if (crud.name === "copy") {
    emit("copy-row-item", rowId.value);
  }
};
</script>

<style lang="scss" scoped>
.show-actions {
  .handlers-button {
    background-color: #5b7aff;
    color: #fff;
    border-radius: 8px;
    padding: 0 15px 0 20px;
    height: 40px;

    .handlers-button-content {
      display: flex;
      justify-content: center;
      width: fit-content;
      font-weight: 600;
      align-items: center;
    }
  }

  .action-item--disabled {
    cursor: not-allowed;
  }
}
</style>

<style lang="scss">
.action-item--disabled {
  background: rgba(138, 144, 153, 0.08) !important;
  color: #8a9099 !important;
  cursor: not-allowed;

  &:hover {
    background: rgba(138, 144, 153, 0.12) !important;
  }

  .q-item__label {
    color: #8a9099 !important;
  }
}

.action-disable-tooltip {
  background: var(--index-grid-active-row-background) !important;
  color: #ffffff !important;
  border-radius: 6px;
  font-size: 12px;
  line-height: 16px;
  padding: 12px 16px;
}
</style>
